import { useEffect, useState } from "react";
import { ExternalLink, RefreshCw } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Eyebrow, Reveal, Rich } from "./Motion";
import { api } from "../lib/api";

const SOURCE_COLORS = {
  "The Loadstar": "#e879f9",
  "gCaptain": "#38bdf8",
  "FreightWaves": "#34d399",
  "Splash247": "#fbbf24",
  "Hellenic Shipping News": "#f87171",
};

const fmtDate = (iso, lang) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(lang === "en" ? "en-GB" : lang, { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "";
  }
};

export const News = () => {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const load = () => api.get("/news").then((r) => setItems(r.data?.items || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => {
    load();
    const iv = setInterval(() => { load(); setTick((v) => v + 1); }, 60000);
    return () => clearInterval(iv);
  }, []);
  return (
    <section id="news" data-testid="news-section" className="relative py-24 lg:py-40 bg-[var(--bone)] text-[var(--ink)]">
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-8 mb-12 lg:mb-16">
          <div className="lg:col-span-7">
            <Eyebrow className="!text-[#b08d1f] [&>span]:!bg-[#b08d1f]">{t.news.eyebrow}</Eyebrow>
            <Reveal><h2 className="mt-8 display text-4xl sm:text-5xl lg:text-7xl leading-[1.06] tracking-[-0.01em]"><Rich text={t.news.title} /></h2></Reveal>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 self-end">
            <p className="text-base lg:text-lg text-[rgba(16,16,20,0.75)]">{t.news.intro}</p>
            <p data-testid="news-live" className="mono text-[11px] uppercase text-[var(--fux)] mt-4 flex items-center gap-2">
              <RefreshCw className="w-3.5 h-3.5" /> {t.news.updated} · {tick ? `${tick}×` : "LIVE"}
            </p>
          </div>
        </div>
        {loading ? (
          <p className="mono text-sm">…</p>
        ) : (
          <div data-testid="news-grid" className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.slice(0, 9).map((n, i) => (
              <Reveal key={n.link} delay={(i % 3) * 0.06}>
                <a data-testid={`news-card-${i}`} href={n.link} target="_blank" rel="noopener noreferrer"
                  className="group flex flex-col h-full bg-white/60 border border-[rgba(16,16,20,0.12)] rounded-[6px] p-6 hover:border-[var(--fux)] transition-colors duration-300">
                  <span className="flex items-center justify-between">
                    <span className="mono text-[10px] uppercase px-2 py-1 rounded-sm text-white" style={{ background: SOURCE_COLORS[n.source] || "#101014" }}>{n.source}</span>
                    <span className="mono text-[11px] text-[rgba(16,16,20,0.6)]">{fmtDate(n.date, lang)}</span>
                  </span>
                  <h3 className="mt-4 text-lg font-semibold leading-snug tracking-tight group-hover:text-[var(--fux)] transition-colors duration-300">{n.title}</h3>
                  {n.summary && <p className="mt-2 text-sm text-[rgba(16,16,20,0.7)] line-clamp-3">{n.summary}…</p>}
                  <span className="mt-auto pt-4 mono text-[11px] uppercase text-[#b08d1f] flex items-center gap-1.5">{t.news.read}<ExternalLink className="w-3.5 h-3.5" /></span>
                </a>
              </Reveal>
            ))}
            {!items.length && <p className="mono text-sm col-span-3">—</p>}
          </div>
        )}
      </div>
    </section>
  );
};
